import React, { useState, useRef, useEffect } from 'react';
import './PlayerDetails.css';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useUser } from "../context/UserContext";
import { hasPrivilege } from '../utils/hasPrivilege';
import ReactDOM from 'react-dom';
const DropdownPortal = ({ children }) => {
  return ReactDOM.createPortal(children, document.body);
}
const PlayerDetails = () => {
  const dropdownMenuRef = useRef(null);
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [children, setChildren] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [openBatchId, setOpenBatchId] = useState(null);
  const [openChildId, setOpenChildId] = useState(null);
  const { userData } = useUser();
  const token = localStorage.getItem('auth-token');
  const childWrapperRef = useRef({});
  const location = useLocation();
  const familyId = location.state?.userId;

  useEffect(() => {
    if (!familyId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL_API_FAMILY}/${familyId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
        if (!response.ok) throw new Error('Network response was not ok');
        const data = await response.json();

        if (data.success) {
          const p = data.data.playerParent;
          setPlayer({
            id: p.id,
            name: p.name,
            mobile: p.mobile_number || 'NA',
            gender: p.genderType ? p.genderType.charAt(0).toUpperCase() + p.genderType.slice(1).toLowerCase() : 'NA',
            dob: p.dob || 'NA',
            email: p.email || 'NA',
            category: 'NA',
            level: 'NA',
            profile: 'NA',
            parentNumber: p.mobile_number || 'NA',
            parentName: p.name || 'NA',
            relation: 'NA'
          });

          const childrenData = data.data.playerBatches || [];
          setChildren(childrenData.map(c => ({
            id: c.player_id,
            player_user_id: c.player_user_id,
            batchid: c.batch_id,
            name: c.player_name,
            category: c.player_category,
            level: c.player_level,
            mobile: c.parent_mobile_number || 'NA',
            dob: c.dob,
            academy: c.academy_name,
            batch: c.batch_name,
            primary: c.primary,
            rfid: c.rfid,
            status: c.status === 'Yes' ? 'No' : 'Yes',
          })));

          const subs = data.data.playingSubscriptions || [];
          setSubscriptions(subs.map(s => ({
            name: s.playerName,
            phone: s.mobileNumber,
            preferredCenter: s.academyName,
            purchaseDate: s.purchaseDate.split('T')[0],
            joiningDate: s.joiningDate.split('T')[0],
            expiryDate: s.expiryDate.split('T')[0],
            planType: s.planType || 'NA',
            sportType: s.sportName,
            preferredSport: 'NA',
            totalPlayingHours: s.totalHours,
            hoursUsed: s.totalHours - s.hoursRemaining,
          })));

          setError(null);
        } else {
          setError('Failed to fetch data');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [familyId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const key = `${openChildId?.playerId}-${openChildId?.batchId}`;
      const wrapperEl = childWrapperRef.current[key];
      const dropdownEl = dropdownMenuRef.current;

      if (
        openChildId &&
        wrapperEl &&
        !wrapperEl.contains(e.target) &&
        dropdownEl &&
        !dropdownEl.contains(e.target)
      ) {
        setOpenChildId(null);
        setDropdownCoords(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openChildId]);

  const handlePerformance = (event, batchId, playerId) => {
    const url = `/performance?batchId=${batchId}&playerId=${playerId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  const handleAttendance = (event, batchId, playerId) => {
    const url = `/playerAttendance?batchId=${batchId}&playerId=${playerId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  const handleUpdatePayment = (event, batchId, playerId) => {
    const url = `/playerpaymentDetail?batchId=${batchId}&playerId=${playerId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };


  const handleEnable = (event, batchId, playerId) => {
    const url = `/addPlayerPayment?batchId=${batchId}&playerId=${playerId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };
  const handleDelete = async (batchId, playerId) => {
    const url = `${process.env.REACT_APP_API_DELETE_PLAYER}?playerId=${playerId}&batchId=${batchId}`;
    const headers = {
      "Content-Type": "application/json",
      'Authorization': `Bearer ${token}`,
    };

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: headers,
      });

      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Successfully removed",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
        setChildren(prevChildren =>
          prevChildren.map(c => {
            if (c.batchid === batchId && c.id === playerId) {
              return {
                ...c,
                primary: false,
                status: 'Yes'
              };
            }
            return c;
          })
        );
      } else {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: "Failed to delete",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Something went wrong.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setOpenChildId(null);
    }
  };

  const handleSetPrimary = async (batchId, playerId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_MAKE_PRIMARY}?batch_id=${batchId}&player_id=${playerId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Player has been set as primary.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });

      } else {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: "Failed to set primary player.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Something went wrong.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setOpenChildId(null);
    }
  };

  const handleChildOption = (opt, child) => {
    if (opt === 'Performance') {
      handlePerformance(child);
    } else if (opt === 'Attendance') {
      handleAttendance(child);
    } else if (opt === 'UpdatePayment') {
      handleUpdatePayment(child);
    } else if (opt === 'Enable') {
      handleEnable(child.batchid, child.id);
    } else if (opt === 'Remove') {
      handleDelete(child.batchid, child.id);
    } else if (opt === 'MakePrimary') {
      handleSetPrimary(child.batchid, child.id);
    } else {
      alert(`${opt} for ${child.name}`);
    }
    setOpenChildId(null);
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setrowsPerPage] = useState(10);

  const filteredFamily = children.filter(c =>
    Object.values(c).some(value =>
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const handleItemsPerPageChange = (event) => {
    setrowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "ascending",
  });

  const sortData = (data) => {
    const { key, direction } = sortConfig;
    return [...data].sort((a, b) => {
      if (a[key] < b[key]) return direction === "ascending" ? -1 : 1;
      if (a[key] > b[key]) return direction === "ascending" ? 1 : -1;
      return 0;
    });
  };

  const sortedData = sortData(filteredFamily);

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1);
  };

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () => {
    return generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );
  };

  const totalEntries = filteredFamily.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

  const handleLogs = (value, isBulk = false) => {
    if (isBulk) {
      const userIds = children.map(c => c.player_user_id).filter(Boolean);
      if (userIds.length > 0) {
        navigate('/ActivityLog', { state: { playerUserId: userIds.join(',') } });
      } else {
        Swal.fire({
          icon: 'warning',
          title: 'No valid user IDs found',
          toast: true,
          position: 'top-end',
          timer: 3000,
          showConfirmButton: false,
        });
      }
    } else {
      if (value) {
        navigate('/ActivityLog', { state: { playerUserId: value } });
      } else {
        Swal.fire({
          icon: 'warning',
          title: 'User ID not available',
          toast: true,
          position: 'top-end',
          timer: 3000,
          showConfirmButton: false,
        });
      }
    }
  };

  const [dropdownCoords, setDropdownCoords] = useState(null);

  const toggleChildDropdown = (playerId, batchId, event) => {
    if (
      openChildId &&
      openChildId.playerId === playerId &&
      openChildId.batchId === batchId
    ) {
      setOpenChildId(null);
      setDropdownCoords(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      setDropdownCoords({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
      setOpenChildId({ playerId, batchId });
    }
  };

  return (
    <div className="enrollment-container" style={{ overflow: 'visible' }}>
      <>
        <h2 className="section-title" style={{ textAlign: 'left' }}>Family Details</h2>
        {player ? (
          <table className="player-table-details">
            <tbody>
              <tr><th>Name</th><td>{player.name}</td></tr>
              <tr><th>Mobile</th><td>{player.mobile}</td></tr>
            </tbody>
          </table>
        ) : loading ? (
          <div>Loading player details...</div>
        ) : (
          <div>No player data available</div>
        )}

        {children.length > 0 && (
          <div style={{ background: 'white', padding: '10px' }}>
            <div className="search-bar">
              <div className="input-container">
                <input
                  type="text"
                  id="searchInput"
                  placeholder=""
                  className={`search-input ${searchTerm ? "has-value" : ""}`}
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                <label htmlFor="searchInput" className="floating-label">
                  🔍 Search
                </label>
              </div>
              {hasPrivilege(userData, 'ACTIVITY_LOGS', 'ACTIVITY_LOGS_ALL_PLAYER_ACCESS') && (
                <button className="create-button" onClick={() => handleLogs(null, true)}>
                  Activity Logs
                </button>
              )}
            </div>

            <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
              <option value={10}>10 items per page</option>
              <option value={50}>50 items per page</option>
              <option value={75}>75 items per page</option>
              <option value={100}>100 items per page</option>
            </select>


            {/* Children Details */}
            <h2 className="section-title" style={{ textAlign: 'left' }}>Player Details</h2>
            <div style={{ overflowX: 'auto', overflowY: 'visible' }}>
              <table className="player-table-details" >
                <thead>
                  <tr>
                    <th>Name</th><th>Category</th><th>Level</th><th>Mobile</th><th>Date of Birth</th><th>Rf-id</th><th>Academy</th><th>Batch</th><th>Primary</th><th>Removed</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRows.map(c => (
                    <tr key={`${c.id}-${c.batchid}`}>
                      <td>{c.name}</td>
                      <td>{c.category}</td>
                      <td>{c.level}</td>
                      <td>{c.mobile}</td>
                      <td>{c.dob}</td>
                      <td>{c.rfid}</td>
                      <td>{c.academy}</td>
                      <td>{c.batch}</td>
                      <td>{c.primary ? 'True' : 'False'}</td>
                      <td>{c.status}</td>
                      <td>
                        <div
                          className="action-wrapper"
                          ref={el => (childWrapperRef.current[`${c.id}-${c.batchid}`] = el)}
                        >
                          <button
                            className="action-btn-details"
                            onClick={(e) => toggleChildDropdown(c.id, c.batchid, e)}
                          >
                            ⋮
                          </button>

                          {openChildId &&
                            openChildId.playerId === c.id &&
                            openChildId.batchId === c.batchid &&
                            dropdownCoords && (
                              <DropdownPortal>
                                <ul
                                  ref={dropdownMenuRef}
                                  className="dropdown-menu-details"
                                  style={{
                                    position: 'absolute',
                                    top: `${dropdownCoords.top}px`,
                                    left: `${dropdownCoords.left - 40}px`,
                                    zIndex: 9999,
                                    background: '#fff',
                                    border: '1px solid #ccc',
                                    borderRadius: '6px',
                                    listStyle: 'none',
                                    padding: 0,
                                    margin: 0,
                                    minWidth: '160px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                  }}
                                >
                                  <li onClick={(e) => handleAttendance(e, c.batchid, c.id)}>Attendance</li>
                                  <li onClick={(e) => handlePerformance(e, c.batchid, c.id)}>Performance</li>
                                  {userData?.userType === 'ADMIN' && (
                                    <>
                                      <li onClick={(e) => handleUpdatePayment(e, c.batchid, c.id)}>Update Player Payment</li>
                                      {c.status === 'Yes' ? (
                                        <>
                                          <li onClick={(e) => handleEnable(e, c.batchid, c.id)}>Enable</li>
                                          <li onClick={() => handleChildOption('MakePrimary', c)}>Make as Primary Batch</li>
                                        </>
                                      ) : (
                                        <li onClick={() => handleChildOption('Remove', c)}>Remove</li>
                                      )}
                                    </>
                                  )}
                                  {(hasPrivilege(userData, 'ACTIVITY_LOGS', 'ACTIVITY_LOGS_ALL_PLAYER_ACCESS') ||
                                    hasPrivilege(userData, 'ACTIVITY_LOGS', 'ACTIVITY_LOGS_SINGLE_PLAYER_ACCESS')) && (
                                      <li onClick={() => handleLogs(c.player_user_id, false)}>Activity Log</li>
                                    )}
                                </ul>
                              </DropdownPortal>
                            )}

                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pagination">
              <div className="showing-entries">
                Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
              </div>
              <button
                className="prev-button"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              {renderPagination()}
              <button
                className="next-button"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {loading && (
          <div className="premium-loader">
            <div className="progress-track">
              <div className="progress-bar"></div>
            </div>
            <div className="status-text">
              <span>L</span>
              <span>O</span>
              <span>A</span>
              <span>D</span>
              <span>I</span>
              <span>N</span>
              <span>G</span>
            </div>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        {!loading && !error && (
          (!player || (children.length === 0 && subscriptions.length === 0)) && (
            <div className="error-message">No data available</div>
          )
        )}
        {subscriptions.length > 0 && (
          <>
            <h2 className="section-title" style={{ textAlign: 'left', marginTop: '20px' }}>Playing Subscription</h2>
            <div className="table-wrapper">
              <table className="player-table-details">
                <thead>
                  <tr>
                    <th>Name</th><th>Phone</th><th>Center</th><th>Purchase</th><th>Joining</th><th>Expiry</th><th>Plan</th><th>Sport</th><th>Pref Sport</th><th>Total Hrs</th><th>Used Hrs</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((sub, i) => (
                    <tr key={i}>
                      <td>{sub.name}</td>
                      <td>{sub.phone}</td>
                      <td>{sub.preferredCenter}</td>
                      <td>{sub.purchaseDate}</td>
                      <td>{sub.joiningDate}</td>
                      <td>{sub.expiryDate}</td>
                      <td>{sub.planType}</td>
                      <td>{sub.sportType}</td>
                      <td>{sub.preferredSport}</td>
                      <td>{sub.totalPlayingHours}</td>
                      <td>{sub.hoursUsed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </>
    </div>
  );

};

export default PlayerDetails;
